import React from 'react';

import { TextField } from 'Containers/App/types';
import { DRUG_TYPES } from 'Containers/ResultPage/constants';
import { useTextContext } from 'Contexts/textContext';
import { H6, Paragraph } from 'DesignLibrary/atoms';
import { IconBlockProps } from 'DesignLibrary/molecules/IconBlock';
import Plan from 'Models/plan';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { NetworkTier, Recommendation, RxTierNames } from 'Types/entities';
import { getRxFieldAndVars, getRxTierName } from 'Utils/helpers';

import { renderCosts } from '../_shared';
import { PlanCardRow } from '../_shared/styled';
import { ViewPlanPageCard } from '../ViewPlanPageCard';

interface PrescriptionsCardProps {
  client: string;
  recommendation: Recommendation;
  selectedTier?: NetworkTier;
}

export const PrescriptionsCard: React.FC<PrescriptionsCardProps> = ({
  client,
  recommendation,
  selectedTier = 'in_network',
}) => {
  const { retrieveContentfulData } = useTextContext();

  const title = retrieveContentfulData<string>(
    'health_details_section.plan_details.prescriptions_card_title',
  );
  const icon: IconBlockProps = {
    icon: 'Prescription',
    color: 'blue',
  };

  const plan = new Plan(recommendation);

  const rxCoverage = plan.getRxCoverageForTier(selectedTier);

  const renderRxCoverages = () => {
    if (rxCoverage.display !== 'tiers') {
      return null;
    }
    const { tierGroup, isOutOfNetwork } = rxCoverage;

    // A tier only earns a row when it has retail or mail-order pricing to show. A tier that is
    // configured but carries no coverage data would otherwise render its name beside an empty value.
    const tierKeys = ([1, 2, 3, 4, 5] as const)
      .map((n) => `rx_tier_${n}` as keyof RxTierNames)
      .filter((tierKey) => !!(tierGroup?.[tierKey]?.retail || tierGroup?.[tierKey]?.mail_order));

    return tierKeys?.map((tierKey) => {
      const retailTier = tierGroup?.[tierKey]?.retail;
      const mailTier = tierGroup?.[tierKey]?.mail_order;

      const RxTierName = getRxTierName({
        retrieveContentfulData,
        planId: plan.planId,
        tierKey,
        tierGroup,
        isOutOfNetwork,
      });

      const retailConfig = getRxFieldAndVars(retailTier);
      const mailConfig = getRxFieldAndVars(mailTier);

      const isNoCoverage =
        (retailConfig?.isText && mailConfig?.isText) ||
        (retailConfig?.isText && !mailConfig) ||
        (mailConfig?.isText && !retailConfig);

      if (isNoCoverage) {
        /* Show "No Coverage" when :
        - Both Retail and Mail Order is not covered
        - Only Retail available and its not covered
        - Only Mail Order available and its not covered */
        return (
          <PlanCardRow key={tierKey} className="planCardRow" data-testid={`prescription-tier-row-${tierKey}`}>
            <Paragraph data-testid={`prescription-tier-name-${tierKey}`}>{RxTierName}</Paragraph>
            <Paragraph
              data-testid={`prescription-tier-values-${tierKey}`}
              color="--text-black"
              size="small"
              style={{ textAlign: 'right', maxWidth: '60%', marginLeft: 'auto' }}
            >
              <Paragraph
                className="not-covered"
                style={{ display: 'inline' }}
                weight="bold"
                size="small"
                color="--text-black"
              >
                <Text field={'health_details_section.rx_tiers_static_values.no_coverage' as TextField} />
              </Paragraph>
            </Paragraph>
          </PlanCardRow>
        );
      }

      return (
        <PlanCardRow key={tierKey} className="planCardRow" data-testid={`prescription-tier-row-${tierKey}`}>
          <Paragraph data-testid={`prescription-tier-name-${tierKey}`}>{RxTierName}</Paragraph>
          <Paragraph
            data-testid={`prescription-tier-values-${tierKey}`}
            color="--text-black"
            size="small"
            style={{ textAlign: 'right', maxWidth: '60%', marginLeft: 'auto' }}
          >
            {retailConfig && (
              <>
                <H6 style={{ display: 'inline' }}>
                  <Text field={'health_details_section.rx_tiers_static_values.retail_text' as TextField} />
                </H6>{' '}
                <Paragraph style={{ display: 'inline' }} weight="bold" size="small" color="--text-black">
                  {retailConfig.isText ? (
                    <Text field={retailConfig.field as TextField} />
                  ) : (
                    <>
                      <RichText
                        inlineParagraphs
                        field={retailConfig.field as TextField}
                        vars={retailConfig.vars}
                      />
                      {retailTier?.is_rx_deductible_waived === false && ' * '}
                    </>
                  )}
                </Paragraph>
                <br />
              </>
            )}
            {mailConfig && (
              <>
                <H6 style={{ display: 'inline' }}>
                  <Text
                    field={'health_details_section.rx_tiers_static_values.mail_order_text' as TextField}
                  />
                </H6>{' '}
                <Paragraph style={{ display: 'inline' }} weight="bold" size="small" color="--text-black">
                  {mailConfig.isText ? (
                    <Text field={mailConfig.field as TextField} />
                  ) : (
                    <>
                      <RichText
                        inlineParagraphs
                        field={mailConfig.field as TextField}
                        vars={mailConfig.vars}
                      />
                      {mailTier?.is_rx_deductible_waived === false && ' * '}
                    </>
                  )}
                </Paragraph>
              </>
            )}
          </Paragraph>
        </PlanCardRow>
      );
    });
  };

  // The legacy drug-type rows carry in-network values only, so they are shown alongside the
  // in-network tier costs and omitted whenever out-of-network Rx info is being displayed.
  const showInNetworkDrugTypes = rxCoverage.display === 'tiers' && rxCoverage.tierGroup === plan.rxTierGroup;

  // Same shape as noOonCoverage: Contentful drives the copy, the literal is only a fallback for a
  // field that has not been published yet (<Text /> would render an empty row instead).
  const rxInNetworkOnlyText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noRxOonCoverage') ||
    'Rx only covered in-network';
  const rxSeePlanDocumentsText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noShowRxOonCoverage') ||
    'See plan documents for details';
  const noOonCoverageText =
    rxCoverage.display === 'inNetworkOnly' ? rxInNetworkOnlyText : rxSeePlanDocumentsText;

  return (
    <ViewPlanPageCard title={title} icon={icon} titleDataTestId="prescriptions-card-title">
      <>
        {showInNetworkDrugTypes &&
          Object.values(DRUG_TYPES).map((costType, index) => {
            // keeping this logic for NON_FORMULARY_DRUG scenario
            const showDrugType =
              !plan[costType].imputed &&
              (plan[costType].coinsurance !== null || plan[costType].copay !== null);
            if (showDrugType) {
              return renderCosts(plan, client, costType, true, index);
            }
            return null;
          })}

        {rxCoverage.display === 'tiers' ? (
          renderRxCoverages()
        ) : (
          <PlanCardRow className="planCardRow" data-testid="prescription-no-oon-coverage">
            <Paragraph color="--text-black" size="small">
              {noOonCoverageText}
            </Paragraph>
          </PlanCardRow>
        )}
      </>
    </ViewPlanPageCard>
  );
};
